/**
 * Types dùng chung giữa `apps/web`, `apps/admin` và `apps/api`.
 *
 * ⚠️ Trước 2026-09-07 file này re-export từ `payload-types.ts` — bản sinh tự động của
 * Payload CMS. Payload đã bị bỏ (xem `docs/DECISIONS.md`), nên **các type dưới đây là
 * viết tay** và là **nguồn chân lý duy nhất** cho hình dạng dữ liệu.
 *
 * Nguồn thật của schema là Mongoose model trong `apps/api/src/models/`. Khi đổi model
 * thì phải sửa file này **cùng lúc** — không còn `pnpm types` sinh hộ nữa.
 *
 * Quy ước (`CLAUDE.md` §3): tên field tiếng Anh, nhãn hiển thị tiếng Việt (nhãn nằm ở
 * component, không nằm trong type).
 */

// ─────────────────────────────────────────────────────────────────────────────
// Nguyên thuỷ
// ─────────────────────────────────────────────────────────────────────────────

/** MongoDB ObjectId đã serialize sang JSON. */
export type ID = string

/** Field mà Mongoose `timestamps: true` tự thêm. Trả về dạng ISO string qua JSON. */
export interface Timestamps {
  createdAt: string
  updatedAt: string
}

/**
 * Relationship trả về `string` (chỉ id) khi không populate, hoặc object đã populate
 * khi có `?populate=1`. Type này diễn tả cả hai.
 */
export type Ref<T> = ID | T

// ─────────────────────────────────────────────────────────────────────────────
// SEO — dùng chung cho product, post và category
// ─────────────────────────────────────────────────────────────────────────────

export interface SeoMeta {
  /**
   * Tiêu đề trên Google (dòng xanh) — **khác** `title` của sản phẩm/bài viết (H1).
   * Để trống thì web dùng mẫu tiêu đề trong `SiteSettings.titleTemplate`.
   */
  title?: string | null
  description?: string | null
  /** Ảnh OG. `Ref` vì có thể chưa populate. */
  image?: Ref<Media> | null
  /** Từ khoá chính — đầu vào cho `analyzeSeo()` ở `./seo`. */
  focusKeyword?: string | null
  /** Từ khoá phụ (long-tail) nên xuất hiện trong nội dung. */
  secondaryKeywords?: string[]
  /**
   * Canonical tự đặt (URL tuyệt đối). Để trống = trang tự trỏ về chính nó — an toàn
   * nhất. Chỉ điền khi đang cố tình gộp hai URL trùng nội dung.
   */
  canonical?: string | null
  /** `true` = thêm `noindex` → trang không bao giờ lên Google. */
  noindex?: boolean
  /** `true` = thêm `nofollow` cho mọi link trên trang. */
  nofollow?: boolean
  /** Ghi đè tiêu đề/mô tả khi chia sẻ mạng xã hội. Trống thì dùng `title`/`description`. */
  ogTitle?: string | null
  ogDescription?: string | null
  /**
   * JSON-LD tự nhập (2026-09-11) — **chuỗi JSON thô**, không phải object.
   *
   * Lưu dạng chuỗi có chủ ý: người biên tập dán nguyên khối từ công cụ sinh schema, và
   * giữ nguyên văn thì sửa lại đúng thứ họ đã dán. Đổi thành object là mất thứ tự khoá,
   * mất khoảng trắng, và một lỗi cú pháp nhỏ sẽ biến thành "dữ liệu biến mất".
   *
   * Hợp lệ = `JSON.parse` ra object hoặc mảng object (kiểm ở `api/lib/seo-body.ts`).
   * Web parse lại trong `try/catch` — dữ liệu hỏng **không bao giờ** được làm sập trang.
   */
  jsonLd?: string | null
}

/** Một câu hỏi thường gặp — từ 2 câu trở lên thì web sinh schema `FAQPage`. */
export interface FaqItem {
  question: string
  /** Text thuần (không HTML) — schema.org yêu cầu văn bản trả lời. */
  answer: string
}

// ─────────────────────────────────────────────────────────────────────────────
// Media
// ─────────────────────────────────────────────────────────────────────────────

/** Tên các biến thể ảnh mà `apps/api` sinh bằng sharp. */
export type MediaSize = 'thumbnail' | 'card' | 'hero'

/** Kích thước mục tiêu của từng biến thể. Nguồn: `apps/api/src/lib/images.ts`. */
export const MEDIA_SIZES: Record<MediaSize, { width: number; height: number }> = {
  thumbnail: { width: 400, height: 300 },
  card: { width: 600, height: 600 },
  hero: { width: 1200, height: 630 },
}

/**
 * Biến thể mà ảnh cỡ `width`×`height` **không** sinh được.
 *
 * Lặp đúng điều kiện của `apps/api/src/lib/images.ts` (`w < target.width || h <
 * target.height` — api không bao giờ phóng to). Lệch điều kiện là nhãn cảnh báo ở admin
 * nói khác việc server làm, kiểu sai tệ nhất vì không có gì báo.
 */
export function missingVariants(width: number, height: number): MediaSize[] {
  return (Object.keys(MEDIA_SIZES) as MediaSize[]).filter(
    (name) => width < MEDIA_SIZES[name].width || height < MEDIA_SIZES[name].height,
  )
}

/** Cỡ nhỏ nhất còn sinh đủ 3 biến thể. Suy ra từ `MEDIA_SIZES`, không gõ tay. */
export function minSourceSize(): { width: number; height: number } {
  const all = Object.values(MEDIA_SIZES)
  return {
    width: Math.max(...all.map((s) => s.width)),
    height: Math.max(...all.map((s) => s.height)),
  }
}

/**
 * Câu hướng dẫn cỡ ảnh, dùng chung cho mọi ô chọn ảnh trong admin. Một nguồn chữ duy
 * nhất: đổi `MEDIA_SIZES` thì cả 3 chỗ hiển thị tự đúng theo.
 *
 * Khuyến nghị **vuông** ≥ cạnh dài nhất (1200) thay vì đúng 1200×630: ảnh vuông thoả cả
 * ba biến thể mà không cần người dùng nhớ ba con số, và `card` 1:1 là biến thể xuất hiện
 * nhiều nhất trên web (thẻ sản phẩm, lưới danh mục, ảnh đại diện danh mục).
 */
export function mediaSizeHint(): string {
  const side = minSourceSize().width
  const list = (Object.keys(MEDIA_SIZES) as MediaSize[])
    .map((n) => `${n} ${MEDIA_SIZES[n].width}×${MEDIA_SIZES[n].height}`)
    .join(' · ')
  return `Nên ảnh vuông, tối thiểu ${side}×${side}. Hệ tự sinh ${list} — đều cắt giữa.`
}

export interface MediaVariant {
  filename: string
  width: number
  height: number
  filesize: number
  /** URL công khai tuyệt đối trên Garage (cổng 3902). */
  url: string
}

export interface Media extends Timestamps {
  id: ID
  /** Bắt buộc — không có `alt` là mất điểm SEO và mất accessibility. */
  alt: string
  /**
   * Tiêu đề/mô tả của **chính tấm ảnh** (2026-09-11) — tuỳ chọn, khác `alt` là bắt buộc.
   * `title` hiện thành tooltip khi rê chuột; `description` dành cho sitemap ảnh.
   */
  title?: string | null
  description?: string | null
  filename: string
  mimeType: string
  filesize: number
  width: number
  height: number
  /** URL công khai tuyệt đối của ảnh **gốc** trên Garage. */
  url: string
  /**
   * Các biến thể đã sinh. Size nào **lớn hơn ảnh gốc thì không có ở đây** —
   * `apps/api` không upscale (giữ đúng hành vi cũ của Payload).
   *
   * ⚠️ **Cả object có thể vắng mặt.** Model khai cả 3 biến thể `default: undefined`
   * (`apps/api/src/models/Media.ts`), nên ảnh gốc nhỏ hơn cả thumbnail (400×300) thì
   * Mongoose lược bỏ luôn `sizes`. Phải `m.sizes?.thumbnail?.url ?? m.url`; viết
   * `m.sizes.thumbnail` là ném TypeError lúc render.
   */
  sizes?: Partial<Record<MediaSize, MediaVariant>>
}

// ─────────────────────────────────────────────────────────────────────────────
// Category
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Danh mục **có cây phân cấp**. "Loại sản phẩm" là một cấp trong cây này.
 *
 * URL vẫn phẳng — cây không nằm trong đường dẫn. Danh mục **có trang đích riêng**, nên
 * nó mang cả `meta` và `faq` như sản phẩm/bài viết.
 *
 * Từ 2026-09-11 collection này phục vụ **cả** danh mục sản phẩm lẫn danh mục blog, phân
 * biệt bằng `space`. Trang đích là `/san-pham/<slug>` hoặc `/blog/<slug>` — **dùng chung
 * đường dẫn với nội dung**, nên `slug` phải duy nhất trên cả không gian đó.
 */
/**
 * Hai vùng tên URL của site. Trong mỗi vùng, **danh mục và nội dung dùng chung slug** —
 * `/san-pham/moc-khoa` là danh mục còn `/san-pham/clicker-cao-nho` là sản phẩm, và chỉ
 * tra DB mới biết cái nào là cái nào.
 */
export type SlugSpace = 'product' | 'post'

export interface Category extends Timestamps {
  id: ID
  /** `product` → `/san-pham/<slug>` · `post` → `/blog/<slug>`. */
  space: SlugSpace
  name: string
  slug: string
  /** **HTML từ Tiptap, đã sanitize ở API.** Đoạn giới thiệu ngắn ở đầu trang danh mục. */
  description?: string | null
  /** Tiêu đề riêng cho khối nội dung dài. Trống → dùng tên danh mục. */
  longContentTitle?: string
  /** **HTML đã sanitize.** Khối nội dung SEO dưới lưới sản phẩm, có nút "Xem thêm". */
  longContent?: string
  image?: Ref<Media> | null
  /** `null` = danh mục gốc. */
  parent?: Ref<Category> | null
  /** Tổ tiên từ gốc tới cha trực tiếp — API tính, client chỉ đọc. */
  ancestors?: Ref<Category>[]
  order: number
  published: boolean
  /**
   * Có hiện ở dải "Danh mục sản phẩm" trang chủ hay không. Cờ **vị trí** (như `featured` của
   * Product), **không** phải cờ ẩn/hiện thứ hai — tắt thì URL/sitemap/footer/lọc vẫn nguyên.
   * Optional vì doc cũ chưa có field: thiếu = coi như `true`, nên luôn so `!== false`.
   */
  showOnHomepage?: boolean
  /** Từ 2 câu trở lên → trang danh mục sinh schema `FAQPage`. */
  faq?: FaqItem[]
  meta?: SeoMeta | null
}

/**
 * Node của cây trả về từ `GET /api/categories?tree=1`.
 * **Không** mang `meta`/`faq`: cây chỉ để hiển thị menu và bộ lọc. Cần SEO thì gọi
 * `GET /api/categories/:id` hoặc `/slug/:slug`.
 */
export interface CategoryTreeNode {
  id: ID
  name: string
  slug: string
  order: number
  published: boolean
  /** `buildTree()` luôn set (doc cũ thiếu field → `true`), nên ở đây không optional. */
  showOnHomepage: boolean
  parent: ID | null
  image: Media | null
  children: CategoryTreeNode[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Color & Video — bảng dùng chung, sản phẩm tham chiếu
// ─────────────────────────────────────────────────────────────────────────────

export interface Color extends Timestamps {
  id: ID
  name: string
  slug: string
  /** `#RRGGBB`, đã uppercase. */
  hex: string
  order: number
}

export type VideoProvider = 'youtube' | 'vimeo'

export interface Video extends Timestamps {
  id: ID
  title: string
  url: string
  provider: VideoProvider
  /** Frontend dựng `src` iframe từ `provider + videoId`, KHÔNG dùng `url` thô. */
  videoId: string
  thumbnailUrl?: string | null
  order: number
}

// ─────────────────────────────────────────────────────────────────────────────
// Product
// ─────────────────────────────────────────────────────────────────────────────

export type StockType = 'in_stock' | 'pre_order' | 'out_of_stock'

export interface PreOrderInfo {
  daysUntilAvailable?: number | null
  /** ISO date string. */
  estimatedDate?: string | null
  note?: string | null
}

/**
 * Một biến thể = tổ hợp (màu × kích cỡ) với **SKU, giá, tồn kho riêng**.
 * Chỉ để **hiển thị** — site không có giỏ hàng (`CLAUDE.md` §3).
 */
export interface ProductVariant {
  id: ID
  sku: string
  /**
   * Tên hiển thị cho khách ở hàng "Phân loại" — ví dụ "Combo nơ tím". **Tuỳ chọn**: trống thì web
   * ghép nhãn từ màu + kích cỡ, không có cả hai thì hiện SKU (`variantLabel()` ở `VariantPicker`).
   */
  label?: string
  /** **Tuỳ chọn** — sản phẩm không có màu vẫn có biến thể (combo, phụ kiện). */
  color?: Ref<Color> | null
  /** **Tuỳ chọn** — xem `label`. */
  size?: string
  /** Số nguyên VND, ghi đè `product.price`. */
  price: number
  compareAtPrice?: number | null
  stockQuantity?: number
  image?: Ref<Media> | null
  order: number
  active: boolean
  /**
   * **API tính, không lưu.** Giá biến thể sau khi áp `product.discount` — chỉ có khi
   * `product.onSale === true`, ngược lại `null`.
   */
  salePrice?: number | null
}

/**
 * Giảm giá theo **phần trăm, có lịch**. `price` (và giá từng biến thể) vẫn là giá gốc;
 * API tính `salePrice` lúc trả JSON. Để trống `startsAt`/`endsAt` = vô thời hạn.
 * Hết hạn thì `onSale` tự về `false` — không cần ai vào sửa.
 */
export interface ProductDiscount {
  /** 1–90. */
  percent: number
  /** ISO date-time. */
  startsAt?: string | null
  endsAt?: string | null
}

/**
 * Thông số kỹ thuật — khối "Thông số" trên trang sản phẩm.
 *
 * ⚠️ **Đơn vị nằm trong tên field:** `…Cm` = centimet, `weightG` = gram. Đừng đổi đơn vị mà giữ tên.
 * Kích thước chỉ hiện trên web khi có **đủ** ba chiều — `5 × ? × 2` là số vô nghĩa. Dùng
 * `formatDimensions()` / `formatWeight()` ở `./product`, đừng tự ghép chuỗi ở từng chỗ.
 */
export interface ProductSpecs {
  lengthCm?: number | null
  widthCm?: number | null
  heightCm?: number | null
  weightG?: number | null
  /** Tag tự gõ ở admin, không phải enum. Web nối bằng `, `. */
  materials?: string[]
  technologies?: string[]
  accessories?: string[]
}

export interface Product extends Timestamps {
  id: ID
  title: string
  slug: string
  /** SKU cấp sản phẩm — đối chiếu Shopee. Biến thể có SKU riêng. */
  sku?: string
  /** **Số nguyên VND.** Giá cơ bản; có biến thể thì web hiện "từ …" và JSON-LD dùng `AggregateOffer`. */
  price: number
  /** Giá gốc trước giảm — chỉ để hiện badge. */
  compareAtPrice?: number | null
  stockType: StockType
  /** Chỉ có nghĩa khi `stockType === 'in_stock'` và không có biến thể. */
  stockQuantity?: number | null
  /** Chỉ có nghĩa khi `stockType === 'pre_order'`. */
  preOrder?: PreOrderInfo | null
  /** Màu có sẵn — tham chiếu bảng `colors`. */
  colors?: Ref<Color>[]
  /** HTML từ Tiptap, đã sanitize ở API. Render bằng `dangerouslySetInnerHTML` an toàn. */
  description: string
  /** Text thuần 1–2 câu cho card. */
  shortDescription?: string
  images?: Ref<Media>[]
  videos?: Ref<Video>[]
  variants?: ProductVariant[]
  category?: Ref<Category> | null
  tags?: string[]
  shopeeUrl?: string | null
  zaloLink?: string | null
  /** Lên section "Sản phẩm nổi bật" ở trang chủ. Khác với nhãn HOT/MỚI. */
  featured?: boolean
  /** Nhãn HOT — mask góc phải ảnh + ưu tiên sort. Xem `productBadges()` ở `./product`. */
  isHot?: boolean
  /**
   * Nhãn MỚI. Tên là `isNewArrival` vì `isNew` là path Mongoose đã chiếm
   * (`doc.isNew` = cờ "chưa lưu") — đặt trùng làm Mongoose cảnh báo và có thể vỡ hook.
   */
  isNewArrival?: boolean
  /** Thứ tự tay trong danh sách (nhỏ lên trước). Dùng khi `sort=priority`. */
  order?: number
  discount?: ProductDiscount | null
  /** `Product.brand` của schema.org — Google khuyến nghị. Trống thì web dùng tên shop. */
  brand?: string | null
  /** Câu hỏi thường gặp → schema `FAQPage`, chiếm thêm chỗ trên trang kết quả. */
  faq?: FaqItem[]
  /** Kích thước · trọng lượng · chất liệu · công nghệ · phụ kiện. Xem `ProductSpecs`. */
  specs?: ProductSpecs | null
  /**
   * **Ba field dưới do API tính lúc trả JSON, không lưu DB, không gửi lên khi ghi.**
   * `onSale` = `discount` có hiệu lực tại thời điểm request. `salePrice` = giá cơ bản
   * sau giảm (làm tròn nghìn). Web: khi `onSale` thì hiện `salePrice` và gạch `price`;
   * `compareAtPrice` chỉ dùng khi **không** `onSale`.
   */
  onSale?: boolean
  salePrice?: number | null
  discountPercent?: number | null
  /**
   * Cơ chế publish **duy nhất** = "hiển thị trên web". Trước đây tồn tại song song
   * `published` và `_status` của Payload drafts — đã bỏ `_status` khi rời Payload
   * (đóng nợ kỹ thuật #2). Không thêm flag ẩn/hiện thứ hai.
   */
  published: boolean
  meta?: SeoMeta | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Post (blog)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @deprecated Danh mục blog là `Category` có `space: 'post'` từ 2026-09-11.
 * Kiểu này chỉ còn để migration đọc dữ liệu cũ.
 */
export type PostCategory =
  | '3d_tips'
  | 'printing_guide'
  | 'material'
  | 'case_study'
  | 'technology'

export interface Post extends Timestamps {
  id: ID
  title: string
  slug: string
  excerpt?: string | null
  /** HTML từ Tiptap, đã sanitize ở API. */
  content: string
  featuredImage?: Ref<Media> | null
  /** Danh mục blog — `Category` có `space: 'post'`. Trang đích `/blog/<category.slug>`. */
  category?: Ref<Category> | null
  /**
   * Tác giả — ref tới `User` (nguồn chân lý từ 2026-09-23).
   * Trang đích `/tac-gia/<slug>`; `populate` cho ra `PublicAuthor`.
   */
  authorUser?: Ref<PublicAuthor> | null
  /**
   * @deprecated Tác giả dạng chữ tự do (trước 2026-09-23). Chỉ còn là **fallback hiển thị**
   * cho bài chưa migrate — API không nhận ghi field này nữa. Dùng `authorUser`.
   */
  author?: string | null
  tags?: string[]
  /** ISO date string. */
  publishedAt?: string | null
  published: boolean
  /** Câu hỏi thường gặp trong bài → schema `FAQPage`. */
  faq?: FaqItem[]
  meta?: SeoMeta | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Redirect (điều hướng 301)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Một dòng điều hướng. `from` luôn là đường dẫn nội bộ có `/` đầu; `to` có thể là đường
 * dẫn nội bộ hoặc URL tuyệt đối ra ngoài.
 *
 * `source: 'auto'` = API tự ghi khi slug đổi. `hits` để biết dòng nào còn ai dùng mà dọn.
 */
export interface Redirect extends Timestamps {
  id: ID
  from: string
  to: string
  code: 301 | 302
  source: 'auto' | 'manual'
  note?: string
  hits?: number
  lastHitAt?: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Quà tặng — trang HTML thô, ngoài hệ SEO
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Trang chúc mừng tặng khách, phục vụ ở `/qua-tang/<slug>`.
 *
 * ⚠️ `html` là **nguyên văn** file HTML admin dán vào (artifact: `<head>`, `<style>`,
 * `<script>` inline, ảnh base64). Nó **không** đi qua `lib/sanitize.ts` — đây là ngoại lệ
 * có chủ ý của `CLAUDE.md` §3, xem `docs/DECISIONS.md` 2026-10-12. Ba thứ giữ cho nó an
 * toàn là MỘT gói: đường ghi `requireAdmin`, `CSP: sandbox` lúc phục vụ, và `noindex`.
 * Gỡ một cái mà giữ hai cái kia là mở XSS trên tên miền chính.
 *
 * Cố ý **không có** `meta.*`/`faq`: trang này không vào engine SEO, không vào sitemap.
 */
export interface Gift extends Timestamps {
  id: ID
  /** Tiêu đề hiện trên tab trình duyệt — API ghi vào thẻ `<title>` của `html` lúc lưu. */
  title: string
  slug: string
  html: string
  /** Dung lượng `html` (byte), API ghi sẵn — màn danh sách admin không trả `html`. */
  htmlBytes: number
  /** Ghi chú nội bộ (tặng ai, dịp gì) — không bao giờ render ra web. */
  note?: string
  published: boolean
}

/**
 * Một dòng trong danh sách admin.
 *
 * ⚠️ `GET /api/gifts` cố ý **không** trả `html` (`.select('-html')`) — nếu khai kiểu đầy
 * đủ `Gift` ở đây thì `tsc` im lặng cho qua `row.html.length` và lỗi chỉ lộ lúc chạy.
 * Đúng cái bẫy "kiểu nói dối về dữ liệu thật" ở `CLAUDE.md` §7.
 */
export type GiftListItem = Omit<Gift, 'html'>

/** Body gửi lên `POST`/`PATCH /api/gifts`. `slug` trống = server tự sinh từ `title`. */
export interface GiftBody {
  title: string
  slug?: string
  html: string
  note?: string
  published?: boolean
}

// ─────────────────────────────────────────────────────────────────────────────
// Cấu hình SEO toàn site (singleton `settings`)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Thiết lập SEO dùng cho **mọi** trang. Admin sửa ở `/settings`, `apps/web` đọc để
 * dựng `<title>`, JSON-LD `Organization`, robots mặc định…
 *
 * Lý do tách khỏi code: tên shop, mẫu tiêu đề, mô tả mặc định là thứ người làm SEO
 * muốn đổi mà không cần deploy.
 */
export interface SiteSettings extends Timestamps {
  id: ID
  /** Tên thương hiệu, dùng trong `%sitename%` và schema `Organization.name`. */
  siteName: string
  /** Mẫu tiêu đề, vd `%title% | %sitename%`. Xem `renderTitleTemplate()` ở `./seo`. */
  titleTemplate: string
  titleSeparator: string
  /** Meta description dự phòng khi trang không tự đặt. */
  defaultMetaDescription?: string | null
  defaultOgImage?: Ref<Media> | null
  /** Tắt để chặn Google toàn site (dùng khi site đang dựng). */
  allowIndexing: boolean
  /** Thương hiệu mặc định cho sản phẩm không tự điền `brand`. */
  defaultBrand?: string | null
  /** URL tuyệt đối của web, vd `https://tiemnhanho.vn` — dùng cho canonical + sitemap. */
  siteUrl?: string | null
  /** Mã xác minh Google Search Console (`google-site-verification`). */
  googleSiteVerification?: string | null
  /** ID Google Analytics 4 (`G-XXXXXXX`). Trống = không nhúng. */
  gaMeasurementId?: string | null
  /** Thông tin doanh nghiệp cho schema `Organization` / `LocalBusiness`. */
  organization?: {
    legalName?: string | null
    phone?: string | null
    email?: string | null
    address?: string | null
    /** Link mạng xã hội → `Organization.sameAs`. */
    sameAs?: string[]
  } | null
  /**
   * Menu chính của web. **Rỗng = dùng danh sách viết cứng** ở `apps/web/lib/site.ts`
   * làm dự phòng — cài đặt này không âm thầm xoá trắng menu khi chưa ai khai nó.
   */
  mainNav?: NavItemSetting[]

  /**
   * SEO **riêng cho trang chủ** (2026-09-11).
   *
   * Trang chủ trước đây chỉ đi theo công tắc `allowIndexing` toàn site, không có ô riêng
   * như mọi trang khác. Nó nằm ở `settings` chứ không phải một document riêng — xem
   * `docs/DECISIONS.md` [2026-09-11].
   */
  home?: SeoMeta | null

  /**
   * Đường dẫn chặn crawl trong `robots.txt`. **Rỗng = dùng danh sách mặc định** ở
   * `apps/web` (`/api/`, `/tai-khoan`, `/gio-hang`, `/tim-kiem`) — cùng quy ước với
   * `mainNav`: lỡ xoá trắng trong admin không được biến thành mở toang cho Google.
   */
  robotsDisallow?: string[]
  /** Dòng tự do nối vào cuối `robots.txt` (`Crawl-delay`, sitemap ảnh…). */
  robotsExtra?: string | null
  /** Đường dẫn loại khỏi `sitemap.xml`. Kết thúc bằng `/` = loại cả nhánh. */
  sitemapExclude?: string[]

  /**
   * Dải ảnh "Cảm hứng từ Nhà Nhỏ" ở trang chủ (2026-09-23).
   *
   * ⚠️ Đây là **nội dung**, không phải SEO — `settings` từ nay không còn thuần là cấu hình SEO.
   * Nó nằm đây chứ không phải một collection riêng vì cùng lý do với `mainNav` và `home`: một
   * danh sách ngắn, chỉ dùng ở đúng một chỗ, và đã có sẵn đường xoá cache (`revalidateOnWrite`
   * map `settings` → tag `settings`).
   *
   * **Rỗng = dùng 11 ảnh viết cứng** ở `apps/web/content/homepage.ts` làm dự phòng — cùng quy
   * ước `mainNav`/`robotsDisallow`: lỡ xoá trắng trong admin không được làm mất cả một khối
   * của trang chủ.
   */
  homeInspiration?: HomeInspirationImage[]
}

/** Một mục menu: `href` có thể là đường dẫn nội bộ hoặc URL ra ngoài. */
export interface NavItemSetting {
  label: string
  href: string
  order?: number
}

/**
 * Một ô trong dải "Cảm hứng từ Nhà Nhỏ": **ảnh gốc + khung cắt + ảnh vuông đã sinh**.
 *
 * Ba nhóm field ba vai trò khác nhau, đừng lẫn:
 *   · `media` — ảnh GỐC trong thư viện. Giữ lại để mở ra cắt lại được.
 *   · `crop`  — khung người dùng đã chọn. Giữ lại để lần sau mở ra đúng khung cũ.
 *   · `filename`/`url`/`width`/`height` — ảnh vuông **API tự sinh** bằng sharp. Client gửi lên
 *     là bị Zod loại: cho client tự đặt `url` nghĩa là cho nó nhét ảnh bất kỳ vào trang chủ.
 *
 * `alt` **không có ở đây** — lấy từ `media.alt` (Media đã bắt buộc `alt`). Hai nguồn alt cho
 * cùng một tấm ảnh là cách chắc chắn để chúng lệch nhau.
 */
export interface HomeInspirationImage {
  media: Ref<Media>
  crop: {
    /** Xoay ảnh gốc trước khi cắt. Chỉ 4 mức — user chốt 23/09, không xoay tự do. */
    rotate: 0 | 90 | 180 | 270
    /** Toạ độ + cạnh ô vuông, tính bằng pixel trên ảnh **đã xoay**. */
    x: number
    y: number
    size: number
  }
  /** Key trên Garage của ảnh vuông đã cắt. */
  filename: string
  url: string
  width: number
  height: number
}

// ─────────────────────────────────────────────────────────────────────────────
// User
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Thang bậc quyền — **admin ⊃ manager ⊃ editor**. Xếp hạng thật nằm ở
 * `apps/api/src/middleware/auth.ts` (`RANK`); ở đây chỉ là union cho frontend.
 *
 * ⚠️ Phải khớp `USER_ROLES` trong `apps/api/src/models/User.ts` — `packages/shared`
 * viết tay, không sinh ra từ schema.
 */
export type UserRole = 'admin' | 'manager' | 'editor'

/**
 * User **như API trả về** — không bao giờ chứa `passwordHash`.
 * Hash chỉ tồn tại trong Mongoose model ở `apps/api`, và bị loại khỏi mọi response.
 */
export interface User extends Timestamps {
  id: ID
  email: string
  name: string
  role: UserRole
  /** `/tac-gia/<slug>`. `null` = chưa có trang tác giả công khai. */
  slug?: string | null
  avatar?: Ref<Media> | null
  /** Chức danh hiển thị ("Thợ handmade"). Khác `role` — xem `models/User.ts`. */
  jobTitle?: string | null
  /** Text thuần, không HTML — xem `apps/api/src/models/User.ts`. */
  bio?: string | null
  socials?: AuthorSocials
}

/** Link mạng xã hội của tác giả → `Person.sameAs[]`. URL tuyệt đối. */
export interface AuthorSocials {
  facebook?: string | null
  tiktok?: string | null
  shopee?: string | null
}

/**
 * Tác giả **như API công khai trả về** (`/api/authors`).
 *
 * Cố ý **KHÔNG** có `email`, `role`, `tokenVersion`. Giữ nó tách hẳn khỏi `User` để
 * không ai lỡ tay render email của nhân sự ra trang công khai — `apps/web` chỉ biết
 * kiểu này. Ranh giới thật nằm ở `apps/api/src/lib/author.ts`.
 */
export interface PublicAuthor extends Timestamps {
  id: ID
  name: string
  slug: string
  avatar?: Media | null
  /** Chức danh → schema.org `Person.jobTitle`. */
  jobTitle?: string | null
  bio?: string | null
  socials?: AuthorSocials
  /** Số bài **đã publish**. Có ở endpoint list — `sitemap.ts` lọc bằng nó. */
  postCount?: number
  /** `publishedAt` của bài mới nhất — `lastModified` trong sitemap. */
  lastPostAt?: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Type đã populate — dùng ở apps/web khi query có `populate`
// ─────────────────────────────────────────────────────────────────────────────

/** Category đã populate ảnh, cha và tổ tiên — trang `/danh-muc/<slug>`. */
export type CategoryPopulated = Omit<Category, 'image' | 'parent' | 'ancestors'> & {
  image?: Media | null
  parent?: Category | null
  /** Từ gốc tới cha trực tiếp, đúng thứ tự — dùng dựng breadcrumb. */
  ancestors?: Category[]
}

/** Product đã populate ảnh + category. */
export type ProductPopulated = Omit<Product, 'images' | 'category' | 'colors' | 'videos' | 'variants'> & {
  images?: Media[] | null
  category?: (Omit<Category, 'ancestors' | 'parent'> & { ancestors?: Category[]; parent?: Category | null }) | null
  colors?: Color[]
  videos?: Video[]
  variants?: (Omit<ProductVariant, 'color' | 'image'> & { color?: Color | null; image?: Media | null })[]
}

/** Post đã populate ảnh đại diện **và danh mục**. */
export type PostPopulated = Omit<Post, 'featuredImage' | 'category' | 'authorUser'> & {
  featuredImage?: Media | null
  category?: Category | null
  authorUser?: PublicAuthor | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Response phân trang
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hình dạng response phân trang của `apps/api`.
 *
 * Giữ **đúng** shape cũ của Payload REST API dù đã bỏ Payload — `apps/web` đang dùng
 * (`lib/api/client.ts`), đổi là phải sửa cả frontend mà không được gì.
 */
export interface PaginatedDocs<T> {
  docs: T[]
  totalDocs: number
  limit: number
  totalPages: number
  page: number
  pagingCounter: number
  hasPrevPage: boolean
  hasNextPage: boolean
  prevPage: number | null
  nextPage: number | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Nhãn tiếng Việt + map sang schema.org
// ─────────────────────────────────────────────────────────────────────────────

export const STOCK_LABEL: Record<StockType, string> = {
  in_stock: 'Còn hàng',
  pre_order: 'Đặt trước',
  out_of_stock: 'Hết hàng',
}

/** Dùng cho JSON-LD `Product.offers.availability` (`CLAUDE.md` §5). */
export const STOCK_SCHEMA: Record<StockType, string> = {
  in_stock: 'https://schema.org/InStock',
  pre_order: 'https://schema.org/PreOrder',
  out_of_stock: 'https://schema.org/OutOfStock',
}

export const POST_CATEGORY_LABEL: Record<PostCategory, string> = {
  '3d_tips': 'Mẹo in 3D',
  printing_guide: 'Hướng dẫn in',
  material: 'Chất liệu',
  case_study: 'Câu chuyện Nhà Nhỏ',
  technology: 'Công nghệ',
}

// ─────────────────────────────────────────────────────────────────────────────
// Type guard
// ─────────────────────────────────────────────────────────────────────────────

/** `true` nếu relationship đã được populate (không còn là id dạng string). */
export function isPopulated<T extends object>(
  value: Ref<T> | null | undefined,
): value is T {
  return typeof value === 'object' && value !== null
}

/** Lấy Media đã populate từ một relationship, hoặc `null`. */
export function asMedia(value: Ref<Media> | null | undefined): Media | null {
  return isPopulated(value) ? value : null
}

/** Lấy id từ một relationship, dù đã populate hay chưa. */
export function refId(value: Ref<{ id: ID }> | null | undefined): ID | null {
  if (!value) return null
  return isPopulated(value) ? value.id : value
}
